import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationsService } from '../../services/notifications.service';
import { Notification, NotificationSummary } from '../../models/notification.models';

@Component({
    selector: 'app-notification-dropdown',
    templateUrl: './notification-dropdown.component.html',
    styleUrls: ['./notification-dropdown.component.scss'],
    standalone: false
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() notificationClick = new EventEmitter<Notification>();

  notificationSummary: NotificationSummary = {
    unreadCount: 0,
    hasMore: false,
    notifications: []
  };

  loading: boolean = false;
  
  private destroy$ = new Subject<void>();

  constructor(private notificationsService: NotificationsService) { }

  ngOnInit(): void {
    // Subscribe to notification summary updates
    this.notificationsService.notificationSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((summary: NotificationSummary) => {
        this.notificationSummary = summary;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNotificationClick(notification: Notification): void {
    // Mark as read if unread
    if (!notification.isRead) {
      this.loading = true;
      this.notificationsService.markAsReadAndUpdate(notification.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loading = false;
        });
    }
    
    this.notificationClick.emit(notification);
    this.closeDropdown();
  }

  onViewAll(): void {
    // Load all notifications in the same popup
    this.loading = true;
    this.notificationsService.loadAllNotifications(this.notificationSummary.unreadCount )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loading = false;
      });
  }

  onMarkAllRead(): void {
    const unreadNotifications = this.notificationSummary.notifications.filter(n => !n.isRead);
    if (unreadNotifications.length === 0) {
      return;
    }

    this.loading = true;
    // Mark all unread notifications as read (implementation depends on backend API)
    const markReadPromises = unreadNotifications.map(notification => 
      this.notificationsService.markAsReadAndUpdate(notification.id).toPromise()
    );

    Promise.all(markReadPromises).then(() => {
      this.loading = false;
    }).catch(error => {
      console.error('Error marking all notifications as read:', error);
      this.loading = false;
    });
  }

  closeDropdown(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  getNotificationMessage(notification: Notification): string {
    return this.notificationsService.getNotificationMessage(notification);
  }

  getTimeAgo(notification: Notification): string {
    // Use the comment's creation time instead of notification creation time
    return this.notificationsService.getTimeAgo(notification.comment.createdOn);
  }

  getTruncatedComment(notification: Notification): string {
    return this.notificationsService.truncateText(notification.comment.commentText);
  }

  get hasNotifications(): boolean {
    return this.notificationSummary.notifications.length > 0;
  }

  get hasUnreadNotifications(): boolean {
    return this.notificationSummary.unreadCount > 0;
  }
}
